import { useEffect, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowRight, ChevronDown, Menu, X } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { PRODUCTS } from '@/lib/products'
import { ROUTES, contactPath, productPath } from '@/lib/constants'
import { cn } from '@/lib/utils'
import { ProductIconTile, StatusBadge } from '@/components/shared/ProductBits'
import LanguageSwitcher, { LanguageSegmented } from './LanguageSwitcher'
import ThemeToggle from './ThemeToggle'
import AppLauncher, { AppLauncherLink } from './AppLauncher'

const navLinkCls = ({ isActive }: { isActive: boolean }) =>
  cn(
    'inline-flex h-10 items-center rounded-xl px-3.5 text-sm font-medium transition-colors hover:bg-accent',
    isActive ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
  )

export default function Header() {
  const { t } = useTranslation(['common', 'products'])
  const [openOnPath, setOpenOnPath] = useState<string | null>(null)
  const [scrolled, setScrolled] = useState(false)
  const { pathname } = useLocation()
  const onProducts = pathname.startsWith(ROUTES.PRODUCTS)
  const mobileOpen = openOnPath === pathname

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Lock body scroll while the mobile drawer is open.
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [mobileOpen])

  return (
    <>
      <header
        className={cn(
          'sticky top-0 z-50 w-full border-b transition-colors',
          scrolled || mobileOpen
            ? 'border-border bg-background/85 backdrop-blur-lg'
            : 'border-transparent bg-background/0'
        )}
      >
        <div className="container flex h-16 items-center gap-2 sm:gap-4">
          {/* Logo */}
          <Link to={ROUTES.HOME} className="flex shrink-0 items-center" aria-label="Tdrem">
            <img
              src="/images/tdrem-logo.png"
              alt="Tdrem — Think · Develop · REMaster"
              className="h-9 w-auto sm:h-11 dark:brightness-[1.8] dark:saturate-50"
            />
          </Link>

          {/* Desktop nav */}
          <nav className="ms-4 hidden items-center gap-1 lg:flex" aria-label="Main">
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger
                className={cn(
                  'group inline-flex h-10 items-center gap-1 rounded-xl px-3.5 text-sm font-medium outline-none transition-colors hover:bg-accent data-[state=open]:bg-accent',
                  onProducts ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
                )}
              >
                {t('nav.products')}
                <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]:rotate-180" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" sideOffset={10} className="w-[640px] rounded-2xl p-3">
                <div className="grid grid-cols-2 gap-1">
                  {PRODUCTS.map((p) => (
                    <DropdownMenuItem key={p.id} asChild className="cursor-pointer items-start rounded-xl p-3 focus:bg-accent">
                      <Link to={productPath(p.slug)} className="flex gap-3">
                        <ProductIconTile product={p} size="sm" />
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-2">
                            <span className="font-semibold">{t(`products:${p.id}.name`)}</span>
                            {p.status !== 'live' && <StatusBadge status={p.status} className="px-1.5 py-0 text-[10px]" />}
                          </span>
                          <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">
                            {t(`products:${p.id}.menuLine`)}
                          </span>
                        </span>
                      </Link>
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuItem asChild className="cursor-pointer rounded-xl bg-muted/60 p-3 focus:bg-accent">
                    <Link to={ROUTES.PRODUCTS} className="flex items-center justify-between gap-3">
                      <span>
                        <span className="block font-semibold">{t('nav.allProducts')}</span>
                        <span className="block text-xs text-muted-foreground">{t('nav.allProductsDesc')}</span>
                      </span>
                      <ArrowRight className="h-4 w-4 flip-rtl" />
                    </Link>
                  </DropdownMenuItem>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
            <NavLink to={ROUTES.ABOUT} className={navLinkCls}>
              {t('nav.about')}
            </NavLink>
            <NavLink to={ROUTES.CONTACT} className={navLinkCls}>
              {t('nav.contact')}
            </NavLink>
          </nav>

          {/* Actions */}
          <div className="ms-auto flex items-center gap-0.5 sm:gap-1">
            <LanguageSwitcher className="px-2 sm:px-2.5" />
            <ThemeToggle />
            <AppLauncher className="ms-1 hidden md:inline-flex" />
            <Link
              to={contactPath({ topic: 'demo' })}
              className="ms-1 hidden h-10 items-center rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 lg:inline-flex"
            >
              {t('actions.requestDemo')}
            </Link>
            <button
              type="button"
              className="inline-flex h-10 w-10 items-center justify-center rounded-xl hover:bg-accent lg:hidden"
              onClick={() => setOpenOnPath((prev) => (prev === pathname ? null : pathname))}
              aria-expanded={mobileOpen}
              aria-controls="mobile-nav"
              aria-label={mobileOpen ? t('nav.close') : t('nav.menu')}
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer — rendered outside <header> so header's backdrop-filter
          does NOT create a fixed-position containing block. */}
      {mobileOpen && (
        <div
          id="mobile-nav"
          onClick={(e) => {
            if ((e.target as HTMLElement).closest('a')) setOpenOnPath(null)
          }}
          className="fixed inset-x-0 bottom-0 top-16 z-50 overflow-y-auto overscroll-contain border-t bg-background animate-in fade-in slide-in-from-top-2 lg:hidden"
        >
          <div className="container space-y-7 py-6 pb-10">
            <section>
              <h2 className="mb-2 px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t('nav.products')}
              </h2>
              <ul className="space-y-1">
                {PRODUCTS.map((p) => (
                  <li key={p.id}>
                    <Link to={productPath(p.slug)} className="flex items-center gap-3 rounded-xl p-2.5 hover:bg-accent">
                      <ProductIconTile product={p} size="sm" />
                      <span className="min-w-0 flex-1">
                        <span className="block font-medium">{t(`products:${p.id}.name`)}</span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {t(`products:${p.id}.category`)}
                        </span>
                      </span>
                      {p.status !== 'live' && <StatusBadge status={p.status} className="shrink-0" />}
                    </Link>
                  </li>
                ))}
                <li>
                  <Link to={ROUTES.PRODUCTS} className="flex items-center justify-between rounded-xl p-2.5 text-sm font-medium text-primary hover:bg-accent">
                    {t('nav.allProducts')}
                    <ArrowRight className="h-4 w-4 flip-rtl" />
                  </Link>
                </li>
              </ul>
            </section>

            <section className="grid grid-cols-2 gap-2">
              <Link to={ROUTES.ABOUT} className="rounded-xl border p-3 text-center font-medium hover:bg-accent">
                {t('nav.about')}
              </Link>
              <Link to={ROUTES.CONTACT} className="rounded-xl border p-3 text-center font-medium hover:bg-accent">
                {t('nav.contact')}
              </Link>
            </section>

            <section>
              <h2 className="mb-2 px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t('launcher.title')}
              </h2>
              <ul className="space-y-1 rounded-2xl border p-1.5">
                {PRODUCTS.map((p) => (
                  <li key={p.id}>
                    <AppLauncherLink product={p} className="rounded-xl p-2 hover:bg-accent" />
                  </li>
                ))}
              </ul>
            </section>

            <section className="space-y-2">
              <h2 className="px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t('language')}
              </h2>
              <LanguageSegmented />
            </section>

            <Link
              to={contactPath({ topic: 'demo' })}
              className="flex h-12 items-center justify-center rounded-xl bg-primary font-semibold text-primary-foreground"
            >
              {t('actions.requestDemo')}
            </Link>
          </div>
        </div>
      )}
    </>
  )
}
